import { invokeFunction } from '@/lib/supabase';
import type { FeedbackContext } from '@/lib/feedbackContext';

/**
 * Feedback categories accepted by the submit-feedback Edge Function.
 *
 * These mirror the CHECK constraint on `public.feedback.category`
 * (migration 20260427000001) — adding a category here without updating
 * the migration will result in 422s from the function.
 */
export const FEEDBACK_CATEGORIES = ['bug', 'feature', 'general'] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

export interface SubmitFeedbackInput {
  category: FeedbackCategory;
  message: string;
  /**
   * Best-effort diagnostic context (YAR-365). Optional at every level: the
   * Edge Function accepts a submission without it, because a report with no
   * context still beats no report.
   */
  context?: FeedbackContext;
}

/**
 * POST /functions/v1/submit-feedback
 *
 * The Edge Function resolves the caller's `public.users.id` and active
 * household server-side, so the client only sends the category and
 * message. The table is service-role only (no anon/authenticated policies),
 * which is why this goes through the function instead of a direct insert.
 */
export async function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<void> {
  const { error } = await invokeFunction('submit-feedback', {
    body: input,
  });
  if (error) throw error;
}
